import groupBy from 'lodash/groupBy';

import {
    AddressType,
    CountryCode,
    DataType,
    EmploymentStatus,
    GlobalAccount,
    GUID,
    PersonOrg,
    PersonOrgAddress,
    PersonOrgLink,
    PersonOrgProfile,
    PersonOrgProfileSaveRequest,
    PersonOrgType,
    SuggestedPersonView,
    Tenant,
    WealthSource,
} from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { encryptData, HOUSEHOLD_ROLE_OPTIONS, splitName } from 'utils';

import {
    Address,
    HouseholdPersonFormValues,
    OrgFormValues,
    PersonFormValues,
    PersonOrgFormValues,
} from './person-form-model';

export const getPersonFormDefaultValues = (person: PersonOrgProfile): PersonFormValues => {
    return {
        ...getCommonPersonOrgFormDefaultValues(person),
        firstName: person.personOrg.firstName?.plainText || '',
        lastName: person.personOrg.lastName?.plainText || '',
        dateOfBirth: person.personOrg.dateOfBirth?.plainText,
        dateOfDeath: person.personOrg.dateOfDeath?.plainText,
        employmentStatus: person.personOrg.employmentStatus as EmploymentStatus[],
        employerName: person.personOrg.employerName,
        occupation: person.personOrg.occupation,
        employerPhone: person.personOrg.employerPhone,
        employerAddress: getAddressFormDefaultValues(person.employerAddress, false),
        employerAffiliatedWithFinancialFirm: person.personOrg.employerAffiliatedWithFinancialFirm,
        employerComplianceOfficerEmail: person.personOrg.employerComplianceOfficerEmail,
        countryOfBirth: person.personOrg.countryOfBirth,
        currentUsCitizen: person.personOrg.currentUsCitizen,
        citizenshipCountries: person.personOrg.citizenshipCountries as CountryCode[],
        taxFilingStatus: person.personOrg.taxFilingStatus?.plainText,
        numberOfDependents: person.personOrg.numberOfDependents,
        registeredWithNationalFuturesAssociation: person.personOrg.registeredWithNationalFuturesAssociation,
        title: person.personOrg.title,
        ndvrEmployee: !!person.personOrg.ndvrEmploymentStatus,
        ndvrEmploymentStatus: person.personOrg.ndvrEmploymentStatus,
        advisor: person.personOrg.advisor,
        otherEmail: person.personOrg.otherEmail?.plainText,
        businessPhone: person.personOrg.businessPhone?.plainText,
    };
};

export const getOrgFormDefaultValues = (org: PersonOrgProfile, links: PersonOrgLink[]): OrgFormValues => {
    return {
        ...getCommonPersonOrgFormDefaultValues(org),
        personOrgType: org.personOrg.personOrgType,
        orgName: org.personOrg.orgName?.plainText || '',
        establishedAt: org.personOrg.orgEstablishedAt?.plainText,
        personOrgSubtype: org.personOrg.personOrgSubtype,
        orgState: org.personOrg.orgState,
        portfolioManagementAuthorization: org.personOrg.portfolioManagementAuthorization,
        withdrawalsAuthorization: org.personOrg.withdrawalsAuthorization,
        orgCountry: org.personOrg.orgCountry,
        members: groupBy(links, 'membershipType'),
    };
};

const getCommonPersonOrgFormDefaultValues = (person: PersonOrgProfile): PersonOrgFormValues => {
    return {
        primaryEmail: person.personOrg.primaryEmail?.plainText,
        personalPhone: person.personOrg.personalPhone?.plainText,
        primaryAddress: getAddressFormDefaultValues(person.primaryAddress, false),
        liquidNetWorth: person.financialInformation?.liquidNetWorth,
        netWorth: person.financialInformation?.netWorth,
        wealthSources: person.personOrg.wealthSources as WealthSource[],
        wealthSourcesOther: person.personOrg.wealthSourcesOther,
        highNetWorth: person.financialInformation?.highNetWorth,
        requiredToBeRegisteredDerivativeTrader: person.personOrg.requiredToBeRegisteredDerivativeTrader,
        subjectOfFinancialDispute: person.personOrg.subjectOfFinancialDispute,
        subjectOfFinancialDisputeDetails: person.personOrg.subjectOfFinancialDisputeDetails,
        subjectOfExchangeInvestigation: person.personOrg.subjectOfExchangeInvestigation,
        subjectOfExchangeInvestigationDetails: person.personOrg.subjectOfExchangeInvestigationDetails,
        section13d:
            person.complianceInformation?.section13DNdvrAssetIdList?.assets === undefined
                ? undefined
                : person.complianceInformation?.section13DNdvrAssetIdList?.assets.length
                  ? 'yes'
                  : 'no',
        section13DNdvrAssetIdList: person.complianceInformation?.section13DNdvrAssetIdList?.assets,
        section16:
            person.complianceInformation?.section16NdvrAssetIdList?.assets === undefined
                ? undefined
                : person.complianceInformation?.section16NdvrAssetIdList?.assets.length
                  ? 'yes'
                  : 'no',
        section16NdvrAssetIdList: person.complianceInformation?.section16NdvrAssetIdList?.assets,
        familyAffiliatedWithFinancialFirm: person.personOrg.familyAffiliatedWithFinancialFirm,
        familyAffiliatedWithFinancialFirmDetails: person.personOrg.familyAffiliatedWithFinancialFirmDetails,

        unusualActivity: person.personOrg.unusualActivity,
        ndvrAffiliated: person.personOrg.ndvrAffiliated,
        archived: person.personOrg.archived,
        mailingAddress: getAddressFormDefaultValues(person.mailingAddress),
        billingAddress: getAddressFormDefaultValues(person.billingAddress),
        totalAssets: person.financialInformation?.totalAssets,
        annualNetIncome: person.financialInformation?.annualNetIncome,
    };
};

const getAddressFormDefaultValues = (address?: PersonOrgAddress, sameAsPrimary?: boolean): Address => {
    return {
        street: address?.street?.plainText,
        street2: address?.street2?.plainText,
        city: address?.city?.plainText,
        state: address?.state,
        postalCode: address?.postalCode?.plainText,
        countryCode: address?.countryCode,
        sameAsPrimary: sameAsPrimary ?? !address,
    };
};

export const createPAM = async (tenantGUID: GUID<Tenant>, personData: PersonFormValues) => {
    const [newPerson] = await CustomerApi({ hideError: true }).createPersonOrg(tenantGUID, {
        personOrgType: PersonOrgType.PERSON,
        defaultLanguageTag: 'en-us',
    });

    if (!newPerson) {
        throw new Error('GUID not found');
    }

    const [response] = await CustomerApi({ hideError: true }).savePersonOrgProfile(
        newPerson.personOrgGUID,
        assemblePersonChangeRequest(newPerson.personOrgGUID, personData),
    );

    return response;
};

// Matches the household drawer's validation cap on a Custom role's description.
const MAX_ROLE_LABEL_LENGTH = 50;

const findHouseholdRole = (value: string | undefined) =>
    HOUSEHOLD_ROLE_OPTIONS.find((option) => option.toLowerCase() === value?.trim().toLowerCase());

// Prefill for adding one of Claire's suggested people: split name, and the household role either
// one of the fixed options (Claire's classification, else a case-insensitive relationship match)
// or Custom with the relationship as the label.
export const toSuggestedPersonFormValues = (suggestion: SuggestedPersonView): HouseholdPersonFormValues => {
    const relationship = suggestion.relationship ?? '';
    const role = findHouseholdRole(suggestion.householdRole) ?? findHouseholdRole(relationship);
    return {
        ...splitName(suggestion.name ?? ''),
        accountRoleType: role ?? 'Custom',
        accountRoleLabel: role ? undefined : relationship.slice(0, MAX_ROLE_LABEL_LENGTH),
    };
};

// Ties a new person back to the note suggestion it came from: the backend marks that suggestion
// converted on create-and-link, and useSavePersonProfile refetches the note's suggestions.
export type PersonLink = { suggestedPersonGUID?: string; noteGUID?: string };

export type SavePersonContext =
    | { mode: 'edit'; personGUID: GUID<PersonOrg> }
    | { mode: 'create-household'; globalAccountGUID: GUID<GlobalAccount>; link?: PersonLink }
    | { mode: 'create-tenant'; tenantGUID: GUID<Tenant> };

export const createOrUpdatePerson = async (context: SavePersonContext, personData: PersonFormValues) => {
    if (context.mode === 'create-tenant') {
        return createPAM(context.tenantGUID, personData);
    }

    let guid = context.mode === 'edit' ? context.personGUID : undefined;
    if (context.mode === 'create-household') {
        const [newPersonGUID] = await CustomerApi({ hideError: true }).createAndLinkPersonOrg(
            context.globalAccountGUID,
            {
                personOrgType: PersonOrgType.PERSON,
                defaultLanguageTag: 'en-us',
                ...(context.link?.suggestedPersonGUID && { suggestedPersonGUID: context.link.suggestedPersonGUID }),
            },
        );
        guid = newPersonGUID ?? undefined;
    }

    if (!guid) {
        throw new Error('GUID not found');
    }

    const [response] = await CustomerApi({ hideError: true }).savePersonOrgProfile(
        guid,
        assemblePersonChangeRequest(guid, personData),
    );

    return response;
};

export type SaveOrgGUIDsType =
    | { globalAccountGUID?: GUID<GlobalAccount>; orgGUID: GUID<PersonOrg> } // update
    | { globalAccountGUID: GUID<GlobalAccount>; orgGUID?: never }; // create

export const createOrUpdateOrg = async ({ orgGUID, globalAccountGUID }: SaveOrgGUIDsType, orgData: OrgFormValues) => {
    let guid = orgGUID;
    if (!orgGUID && globalAccountGUID) {
        const [newOrgGUID] = await CustomerApi({ hideError: true }).createAndLinkPersonOrg(globalAccountGUID, {
            personOrgType: orgData.personOrgType,
            defaultLanguageTag: 'en-us',
        });
        guid = newOrgGUID ?? undefined;
    }

    if (!guid) {
        throw new Error('GUID not found');
    }

    const [response] = await CustomerApi({ hideError: true }).savePersonOrgProfile(
        guid,
        assembleOrgChangeRequest(guid, orgData),
    );

    return response;
};

export const assemblePersonChangeRequest = (
    personGuid: string,
    data: PersonFormValues,
): PersonOrgProfileSaveRequest => {
    const { personOrgUpdateRequest, ...fields } = assemblePersonOrgCommonFields(personGuid, data);

    return {
        ...fields,
        personOrgUpdateRequest: {
            ...personOrgUpdateRequest,
            firstName: encryptData(personGuid, DataType.PERSON_ORG_INFORMATION_FIRST_NAME, data.firstName),
            lastName: encryptData(personGuid, DataType.PERSON_ORG_INFORMATION_LAST_NAME, data.lastName),
            dateOfBirth: encryptData(personGuid, DataType.PERSON_ORG_INFORMATION_DATE_OF_BIRTH, data.dateOfBirth),
            dateOfDeath: encryptData(personGuid, DataType.PERSON_ORG_INFORMATION_DATE_OF_DEATH, data.dateOfDeath),
            otherEmail: encryptData(personGuid, DataType.PERSON_ORG_EMAIL_ADDRESS, data.otherEmail),
            businessPhone: encryptData(personGuid, DataType.PERSON_ORG_PHONE_NUMBER, data.businessPhone),
            employmentStatus: data.employmentStatus,
            employerName: data.employerName,
            occupation: data.occupation,
            employerPhone: data.employerPhone,
            employerAffiliatedWithFinancialFirm: data.employerAffiliatedWithFinancialFirm,
            employerComplianceOfficerEmail: data.employerComplianceOfficerEmail,
            countryOfBirth: data.countryOfBirth,
            currentUsCitizen: data.currentUsCitizen,
            citizenshipCountries: data.citizenshipCountries,
            taxFilingStatus: encryptData(
                personGuid,
                DataType.PERSON_ORG_INFORMATION_TAX_FILING_STATUS,
                data.taxFilingStatus,
            ),
            numberOfDependents: data.numberOfDependents,
            registeredWithNationalFuturesAssociation: data.registeredWithNationalFuturesAssociation,
            title: data.title,
            ndvrEmploymentStatus: data.ndvrEmploymentStatus,
            advisor: data.advisor || false,
        },
        employerAddress: assembleAddressCreateRequest(personGuid, AddressType.EMPLOYER, data.employerAddress),
    };
};

const assembleOrgChangeRequest = (orgGuid: string, data: OrgFormValues): PersonOrgProfileSaveRequest => {
    const { personOrgUpdateRequest, ...fields } = assemblePersonOrgCommonFields(orgGuid, data);

    return {
        ...fields,
        personOrgUpdateRequest: {
            ...personOrgUpdateRequest,
            personOrgSubtype: data.personOrgSubtype,
            orgName: encryptData(orgGuid, DataType.PERSON_ORG_INFORMATION_ORGANIZATION_NAME, data.orgName),
            orgEstablishedAt: encryptData(
                orgGuid,
                DataType.PERSON_ORG_INFORMATION_ORGANIZATION_ESTABLISHED_AT,
                data.establishedAt,
            ),
            orgState: data.orgState,
            orgCountry: data.orgCountry ?? CountryCode.US,
            portfolioManagementAuthorization: data.portfolioManagementAuthorization,
            withdrawalsAuthorization: data.withdrawalsAuthorization,
            advisor: false,
        },
        personOrgLinks:
            Object.values(data.members || {})
                .flat()
                .map((link) => ({
                    memberPersonOrgGUID: link.memberPersonOrgGUID,
                    membershipType: link.membershipType,
                    membershipPercent: link.membershipPercent,
                    authorizedToExecuteDocumentsAndLegalAgreements: link.authorizedToExecuteDocumentsAndLegalAgreements,
                    authorizedForPortfolioManagement: link.authorizedForPortfolioManagement,
                    authorizedForWithdrawals: link.authorizedForWithdrawals,
                })) ?? [],
    };
};

const assemblePersonOrgCommonFields = (personGuid: string, data: PersonOrgFormValues) => {
    return {
        personOrgUpdateRequest: {
            primaryEmail: encryptData(personGuid, DataType.PERSON_ORG_EMAIL_ADDRESS, data.primaryEmail),
            personalPhone: encryptData(personGuid, DataType.PERSON_ORG_PHONE_NUMBER, data.personalPhone),
            wealthSources: data.wealthSources,
            wealthSourcesOther: data.wealthSourcesOther,
            requiredToBeRegisteredDerivativeTrader: data.requiredToBeRegisteredDerivativeTrader,
            subjectOfFinancialDispute: data.subjectOfFinancialDispute,
            subjectOfFinancialDisputeDetails: data.subjectOfFinancialDisputeDetails,
            subjectOfExchangeInvestigation: data.subjectOfExchangeInvestigation,
            subjectOfExchangeInvestigationDetails: data.subjectOfExchangeInvestigationDetails,
            familyAffiliatedWithFinancialFirm: data.familyAffiliatedWithFinancialFirm,
            familyAffiliatedWithFinancialFirmDetails: data.familyAffiliatedWithFinancialFirmDetails,
            unusualActivity: data.unusualActivity || false,
            ndvrAffiliated: data.ndvrAffiliated || false,
            archived: data.archived || false,
        },
        primaryAddress: assembleAddressCreateRequest(personGuid, AddressType.PRIMARY, data.primaryAddress),
        mailingAddress: assembleAddressCreateRequest(personGuid, AddressType.MAILING, data.mailingAddress),
        billingAddress: assembleAddressCreateRequest(personGuid, AddressType.BILLING, data.billingAddress),
        financialInformation: {
            highNetWorth: data.highNetWorth,
            netWorth: data.netWorth || undefined,
            liquidNetWorth: data.liquidNetWorth || undefined,
            totalAssets: data.totalAssets || undefined,
            annualNetIncome: data.annualNetIncome || undefined,
        },
        complianceInformation:
            data.section13DNdvrAssetIdList || data.section16NdvrAssetIdList
                ? {
                      section13DNdvrAssetIdList: data.section13DNdvrAssetIdList,
                      section16NdvrAssetIdList: data.section16NdvrAssetIdList,
                  }
                : undefined,
    };
};

const assembleAddressCreateRequest = (personOrgGUID: string, addressType: AddressType, address?: Address) => {
    // missing or same as primary, skip
    if (!address || address.sameAsPrimary) {
        return;
    }

    // empty address, skip
    if (
        !address.street &&
        !address.street2 &&
        !address.city &&
        !address.state &&
        !address.postalCode &&
        !address.countryCode
    ) {
        return;
    }

    return {
        addressType: addressType,
        street: encryptData(personOrgGUID, DataType.PERSON_ORG_ADDRESS_STREET, address.street)!,
        street2: encryptData(personOrgGUID, DataType.PERSON_ORG_ADDRESS_STREET2, address.street2),
        city: encryptData(personOrgGUID, DataType.PERSON_ORG_ADDRESS_CITY, address.city)!,
        state: address.state,
        postalCode: encryptData(personOrgGUID, DataType.PERSON_ORG_ADDRESS_POSTAL_CODE, address.postalCode)!,
        countryCode: address.countryCode,
    };
};
